import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Updated integrations with logo paths from the public/integrations folder
const integrations = [
  // Databases
  { 
    name: 'Oracle', 
    category: 'RDBMS', 
    color: 'bg-red-100 text-red-600',
    logo: '/integrations/oracle_avatar.svg',
    tags: ['RDBMS']
  },
  { 
    name: 'PostgreSQL', 
    category: 'RDBMS', 
    color: 'bg-blue-100 text-blue-600',
    logo: '/integrations/postgres_avatar.svg',
    tags: ['RDBMS']
  },
  { 
    name: 'MySQL', 
    category: 'RDBMS', 
    color: 'bg-orange-100 text-orange-600',
    logo: '/integrations/mysql_avatar.svg',
    tags: ['RDBMS']
  },
  { 
    name: 'SQL Server', 
    category: 'RDBMS', 
    color: 'bg-indigo-100 text-indigo-600',
    logo: '/integrations/sql-server_avatar.svg',
    tags: ['RDBMS']
  },
  { 
    name: 'MongoDB', 
    category: 'NoSQL', 
    color: 'bg-green-100 text-green-600',
    logo: '/integrations/mongodb_avatar.svg',
    tags: ['NoSQL']
  },
  
  // Analytics & Data Lakes
  { 
    name: 'Delta Lake', 
    category: 'Data Lake', 
    color: 'bg-blue-100 text-blue-600',
    logo: '/integrations/delatlake.png',
    tags: ['Data Lake', 'Analytics & DWH']
  },
  { 
    name: 'Apache Iceberg', 
    category: 'Data Lake', 
    color: 'bg-indigo-100 text-indigo-600',
    logo: '/integrations/Apache_Iceberg_Logo.svg',
    tags: ['Data Lake', 'Analytics & DWH']
  },
  { 
    name: 'Spark', 
    category: 'Analytics', 
    color: 'bg-orange-100 text-orange-600',
    logo: '/integrations/spark_avatar.svg',
    tags: ['Analytics', 'Transformation', 'Analytics & DWH']
  },
  { 
    name: 'Hive', 
    category: 'Analytics', 
    color: 'bg-yellow-100 text-yellow-600',
    logo: '/integrations/hive_avatar.svg',
    tags: ['Analytics', 'Analytics & DWH']
  },
  { 
    name: 'Kafka', 
    category: 'Streaming', 
    color: 'bg-gray-100 text-gray-600',
    logo: '/integrations/kafka_avatar.svg',
    tags: ['Streaming', 'Analytics & DWH']
  },
  { 
    name: 'Snowflake', 
    category: 'DWH', 
    color: 'bg-cyan-100 text-cyan-600',
    logo: '/integrations/snowflake_avatar.svg',
    tags: ['DWH', 'Analytics & DWH']
  },
  { 
    name: 'Airflow', 
    category: 'Orchestration', 
    color: 'bg-blue-100 text-blue-600',
    logo: '/integrations/airflow_avatar.svg',
    tags: ['Orchestration', 'Transformation']
  },
  
  // BI Tools
  { 
    name: 'Tableau', 
    category: 'BI', 
    color: 'bg-blue-100 text-blue-600',
    logo: '/integrations/tableau-icon-svgrepo-com.svg',
    tags: ['BI', 'BI Tools']
  },
  { 
    name: 'PowerBI', 
    category: 'BI', 
    color: 'bg-yellow-100 text-yellow-600',
    logo: '/integrations/powerbi_avatar.svg',
    tags: ['BI', 'BI Tools']
  },
  { 
    name: 'Metabase', 
    category: 'BI', 
    color: 'bg-green-100 text-green-600',
    logo: '/integrations/metabase-svgrepo-com.svg',
    tags: ['BI', 'BI Tools']
  },
  { 
    name: 'Superset', 
    category: 'BI', 
    color: 'bg-red-100 text-red-600',
    logo: '/integrations/Superset_logo.svg',
    tags: ['BI', 'BI Tools']
  },
  { 
    name: 'dbt', 
    category: 'Transformation', 
    color: 'bg-green-100 text-green-600',
    logo: '/integrations/dbt-cloud_avatar.svg',
    tags: ['Transformation']
  }
];

// Define filter categories
const filterCategories = [
  { name: 'All Integrations', filter: 'all' },
  { name: 'RDBMS', filter: 'RDBMS' },
  { name: 'NoSQL', filter: 'NoSQL' },
  { name: 'Analytics & DWH', filter: 'Analytics & DWH' },
  { name: 'BI Tools', filter: 'BI Tools' },
  { name: 'Transformation', filter: 'Transformation' }
];

const Integrations = () => {
  // State to track the active filter
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredItems, setFilteredItems] = useState(integrations);

  // Function to handle filter clicks
  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  // Update filtered items when activeFilter changes
  useEffect(() => {
    const filterIntegrations = () => {
      if (activeFilter === 'all') {
        setFilteredItems(integrations);
        return;
      }

      const filtered = integrations.filter(integration => 
        integration.tags && integration.tags.includes(activeFilter)
      );
      
      setFilteredItems(filtered);
    };
    
    filterIntegrations();
  }, [activeFilter]);

  return (
    <section id="integrations" className="section py-20 bg-gray-50">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-4xl font-bold mb-4"
          >
            Seamless <span className="text-gradient">Integrations</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Qupid connects with your existing data ecosystem to provide comprehensive 
            observability across your entire data platform.
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mb-12"
        >
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {filterCategories.map(category => (
              <button 
                key={category.name}
                onClick={() => handleFilterClick(category.filter)}
                className={`px-4 py-2 rounded-full font-medium transition-colors duration-300 ${
                  activeFilter === category.filter 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-gray-100'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
          <AnimatePresence>
            {filteredItems.map((integration, index) => (
              <motion.div
                key={integration.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }}
                layout
                transition={{ duration: 0.4, delay: 0.05 * (index % 6) }}
                className="flex flex-col items-center"
              >
                <div className={`w-16 h-16 flex items-center justify-center mb-3 p-2 bg-white rounded-lg shadow-sm transition-all duration-300 hover:shadow-md`}>
                  <img 
                    src={integration.logo} 
                    alt={`${integration.name} logo`} 
                    className="max-w-full max-h-full"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className={`hidden w-full h-full ${integration.color} rounded items-center justify-center`}>
                    <span className="font-bold">{integration.name.substring(0, 2)}</span>
                  </div>
                </div>
                <h4 className="font-medium text-center">{integration.name}</h4>
                <span className="text-xs text-gray-500">{integration.category}</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-16 text-center"
        >
          <div className="inline-block bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-xl font-semibold mb-4">Missing an integration?</h3>
            <p className="text-gray-600 mb-4">
              Our team is constantly adding new integrations. If you don't see what you need, let us know!
            </p>
            <a href="/contact" className="button button-secondary">
              Request Integration
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Integrations; 